import React, { type ReactNode } from 'react';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { Icon } from '../icon/Icon';
import styles from './ModalContent.scss';

interface ModalContentProps {
  title?: string;
  subtitle?: string;
  ariaLabel?: string;
  onClose: () => void;
  children: ReactNode;
  scrollableContent?: boolean;
  className?: string;
  titleClassName?: string;
  hideCloseButton?: boolean;
}

export const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(
  (
    {
      title,
      subtitle,
      ariaLabel,
      onClose,
      scrollableContent = true,
      children,
      className,
      titleClassName,
      hideCloseButton = false,
    },
    ref,
  ) => {
    const ariaLabelProp = title
      ? { 'aria-labelledby': 'dialogTitle' }
      : { 'aria-label': ariaLabel };

    const contentClassName = scrollableContent
      ? styles.modalContent
      : `${styles.modalContent} ${styles.modalContentNoScroll}`;

    return (
      <div
        tabIndex={-1}
        {...ariaLabelProp}
        className={cx(contentClassName, className)}
        role="dialog"
        ref={ref}
      >
        {!hideCloseButton && (
          <button
            aria-label="Close modal"
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <Icon name="close" />
          </button>
        )}
        {title && (
          <h1 className={cx(typography.d3, titleClassName)} id="dialogTitle">
            {title}
          </h1>
        )}
        {subtitle && <p className={typography.c2}>{subtitle}</p>}
        {scrollableContent ? (
          children
        ) : (
          <div className={styles.modalContentBody}>{children}</div>
        )}
      </div>
    );
  },
);

ModalContent.displayName = 'ModalContent';
